// Partner companies - the admin section.
//
// Two actions live here rather than on the project pages because
// neither is ordinary project management:
//
//   * converting a partner into a paying company;
//   * letting a contractor edit their own rows on the owner's plan;
//   * (super admin) merging a partner company record into another
//     (MergeCompanyDialog, routes/adminPartnerMerge.js), and undoing it.
//
// The backend (routes/partnerAdmin.js) allows both only for a company
// admin over their own custody chain, or a super admin.
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Table, Tag, Switch, Button, Modal, Select, message, Empty, Tooltip } from "antd";
import { ShopOutlined, SafetyCertificateOutlined, MergeCellsOutlined } from "@ant-design/icons";
import TopNavbar from "../configuration/navbar";
import Sidebar from "../configuration/sidebar";
import CompanyLogo from "components/CompanyLogo/CompanyLogo";
import {
  getPartnerCompanies,
  convertPartnerCompany,
  setPartnerGrants,
} from "api/partnerAdmin/partnerAdminApi";
import { undoMerge } from "api/partnerMerge/partnerMergeApi";
import MergeCompanyDialog from "./MergeCompanyDialog";

export default function PartnerAdminPage() {
  const userInfo = useSelector((s) => s.user.userInfo) || {};
  const isAdmin = !!(userInfo.admin || userInfo.companyAdmin);

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ isMaster: false, superAdmin: false });
  const [loading, setLoading] = useState(true);
  const [convertRow, setConvertRow] = useState(null);
  const [pkg, setPkg] = useState("Coordination");
  const [working, setWorking] = useState(false);
  const [mergeRow, setMergeRow] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getPartnerCompanies()
      .then((d) => {
        setRows(d.companies || []);
        setMeta({ isMaster: !!d.isMaster, superAdmin: !!d.superAdmin });
      })
      .catch((e) => message.error(e.message || "Failed to load partner companies"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isAdmin) load();
    else setLoading(false);
  }, [isAdmin, load]);

  // What a company admin may hand a contractor. Conversion is NOT here:
  // that is a super-admin act (the firm starts paying us and gets its
  // own tenancy).
  const GRANTS = [
    { key: "allowIssueCreate", label: "Raise issues", hint: "Create and import their own issues on this project." },
    { key: "allowChecklistCreate", label: "Add checklist items", hint: "Create their own checklist items." },
    { key: "allowPlanEdit", label: "Edit their plan rows", hint: "Move dates and status on the activities their company is responsible for. Nobody else's." },
    { key: "allowPlanCreate", label: "Create plan activities", hint: "Add their own activities. They stay off the master schedule until you approve them in the Review Center - that is how their plan becomes part of yours." },
    { key: "allowInternalApproval", label: "Internal sign-off first", hint: "Their supervisor signs their team's work off before it reaches you. You still review it." },
    { key: "allowSupervisorInvite", label: "Add their own crew", hint: "Their supervisors maintain their own roster instead of you typing every contact." },
    { key: "allowAreaPropose", label: "Upload / change plan areas", hint: "Bring in a sheet and draw or redraw coordination areas. Off by default - an area is your picture of the job. Even granted, their work waits in your Review Center." },
  ];

  const toggleGrant = async (project, key, allow) => {
    try {
      await setPartnerGrants(project.participantId, { [key]: allow });
      const label = GRANTS.find((g) => g.key === key)?.label || key;
      message.success(`${allow ? "Granted" : "Revoked"}: ${label} on "${project.title}"`);
      load();
    } catch (e) {
      message.error(e.message || "Failed to update");
    }
  };

  const doConvert = async () => {
    if (!convertRow) return;
    setWorking(true);
    try {
      const r = await convertPartnerCompany(convertRow._id, { package: pkg });
      message.success(
        `${convertRow.name} is a company now` +
          (r.projectsProjected?.length
            ? ` - ${r.projectsProjected.length} project(s) now appear in their own workspace`
            : "")
      );
      setConvertRow(null);
      load();
    } catch (e) {
      message.error(e.message || "Conversion failed");
    } finally {
      setWorking(false);
    }
  };

  // Undo from the list, for a merge whose dialog is long closed. A refusal
  // that names rows changed since is shown as is, with "Undo anyway".
  const undoFromList = (r) => {
    const go = async (force) => {
      try {
        const out = await undoMerge(r.mergeRunId, force);
        message.success(`Undone: ${out.restored} document(s) put back. ${r.name} is its own company again.`);
        load();
      } catch (e) {
        if (e.drift && !force) {
          Modal.confirm({
            title: "Changed since the merge",
            content: (
              <div>
                <p>{e.message}</p>
                <ul>{e.drift.slice(0, 20).map((d) => <li key={`${d.collection}:${d._id}`}>{d.label}</li>)}</ul>
              </div>
            ),
            okText: "Undo anyway",
            okButtonProps: { danger: true },
            onOk: () => go(true),
          });
        } else message.error(e.message || "Undo failed");
      }
    };
    Modal.confirm({
      title: `Undo the merge of ${r.name} into ${r.mergedIntoName || "the other company"}?`,
      content: "Every record the merge changed is put back from its backup, and the people who moved go back.",
      okText: "Undo merge",
      onOk: () => go(false),
    });
  };

  const columns = [
    {
      title: "Company",
      dataIndex: "name",
      render: (v, r) => (
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <CompanyLogo logo={r.logo || ""} name={v} size={32} showMissing />
        <div>
          <b>{v}</b>{" "}
          {r.kind === "partner" ? (
            <Tag color="blue">partner</Tag>
          ) : (
            <Tag color="green">company</Tag>
          )}
          {r.selfManagedAt && <Tag color="gold">pays us</Tag>}
          <div style={{ fontSize: 12, color: "#888" }}>
            {r.domains?.length ? r.domains.join(", ") : "no claimed domain"}
            {r.chain?.length ? ` · under ${r.chain.join(" › ")}` : ""}
          </div>
        </div>
        </div>
      ),
    },
    { title: "Members", dataIndex: "members", width: 90 },
    {
      title: "Ceiling",
      dataIndex: "ceiling",
      width: 100,
      render: (v) => <span style={{ color: "#888" }}>{v} keys</span>,
    },
    {
      title: "What they may do on your project",
      key: "grants",
      render: (_, r) =>
        (r.projects || []).length === 0 ? (
          <span style={{ color: "#bbb" }}>no projects</span>
        ) : (
          (r.projects || []).map((p) => (
            <div key={p.participantId} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{p.title}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 18px" }}>
                {GRANTS.map((g) => (
                  <Tooltip key={g.key} title={g.hint}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <Switch
                        size="small"
                        checked={!!p[g.key]}
                        onChange={(on) => toggleGrant(p, g.key, on)}
                      />
                      <span style={{ fontSize: 12, color: "#555" }}>{g.label}</span>
                    </span>
                  </Tooltip>
                ))}
              </div>
            </div>
          ))
        ),
    },
    {
      title: "",
      key: "action",
      width: 150,
      render: (_, r) =>
        r.mergedInto ? (
          <div>
            <Tag color="default">merged into {r.mergedIntoName || "another company"}</Tag>
            {meta.superAdmin && r.mergeRunId && (
              <Button size="small" style={{ marginTop: 6 }} onClick={() => undoFromList(r)}>
                Undo merge
              </Button>
            )}
          </div>
        ) : r.kind !== "partner" ? (
          <Tag icon={<SafetyCertificateOutlined />} color="green">
            converted
          </Tag>
        ) : meta.superAdmin ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-start" }}>
            <Button
              type="primary"
              ghost
              icon={<ShopOutlined />}
              onClick={() => {
                setPkg("Coordination");
                setConvertRow(r);
              }}
            >
              Convert
            </Button>
            <Button size="small" icon={<MergeCellsOutlined />} onClick={() => setMergeRow(r)}>
              Merge into another company...
            </Button>
          </div>
        ) : (
          <Tooltip title="Converting a partner into a paying company is a super-admin action.">
            <span style={{ color: "#bbb", fontSize: 12 }}>super admin only</span>
          </Tooltip>
        ),
    },
  ];

  return (
    <div className="personal-area">
      <TopNavbar handleSearch={() => {}} />
      <div className="content-bg p-2">
        <div className="container d-flex mt-1">
          <Sidebar />
          <div className="profile-content px-0 pt-0">
            <div className="w-100 px-0">
              <div className="d-flex justify-content-between px-3 py-3 border-bottom">
                <div>
                  <h5 className="mb-1">Partner companies</h5>
                  <div style={{ color: "#888", fontSize: 13 }}>
                    Firms you brought onto your projects. Grant them what they
                    need to run their own work - everything they create still
                    lands in your Review Center before it goes live for
                    everyone.{" "}
                    {meta.superAdmin
                      ? "As a super admin you can also convert a partner into a paying company."
                      : "Converting a partner into a company is a super-admin action."}
                    {meta.isMaster && (
                      <Tag color="purple" style={{ marginLeft: 8 }}>
                        master tenancy
                      </Tag>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-3 py-3">
                {!isAdmin ? (
                  <Empty description="Only a company admin or a super admin can administer partner companies" />
                ) : (
                  <Table
                    rowKey="_id"
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    pagination={{ pageSize: 15, showSizeChanger: false }}
                    locale={{ emptyText: <Empty description="No partner companies yet" /> }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {meta.superAdmin && (
        <MergeCompanyDialog
          source={mergeRow}
          open={!!mergeRow}
          onClose={() => setMergeRow(null)}
          onDone={() => load()}
        />
      )}

      <Modal
        title={convertRow ? `Convert ${convertRow.name} to a company` : "Convert"}
        open={!!convertRow}
        okText="Convert"
        confirmLoading={working}
        onOk={doConvert}
        onCancel={() => setConvertRow(null)}
      >
        <div style={{ color: "#555", fontSize: 13, lineHeight: 1.6 }}>
          <p>
            They keep everything: the same people, the same issues, the same
            history. What changes is that they stop being held under your
            account and start running their own - and the projects they worked
            on appear in their own workspace, each firm seeing only the portion
            their people generated.
          </p>
          <p style={{ marginBottom: 6 }}>
            <b>They can then hold partners of their own.</b> They gain no
            authority on your projects: still no participant management, still
            only their own slice of your schedule.
          </p>
          <div style={{ marginTop: 14 }}>
            <span style={{ marginRight: 8 }}>Package</span>
            <Select
              value={pkg}
              style={{ width: 220 }}
              onChange={setPkg}
              options={[
                { value: "Coordination", label: "Coordination (41 keys)" },
                { value: "IssueManagement", label: "Issue Management (54 keys)" },
                { value: "full", label: "Full company set" },
              ]}
            />
            <Tooltip title="Conversion never narrows an existing ceiling - the package is merged with what they already hold.">
              <span style={{ marginLeft: 10, color: "#888", cursor: "help" }}>?</span>
            </Tooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
}
